import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import type { AdminTicketDetailsDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { ThumbsDownIcon } from "@/components/Icons/ThumbsDownIcon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { Trans, useTranslation } from "@/translations";

const FORM_ID = "ticket-ai-quick-denial-modal-form";

type TicketAIQuickDenialModalProps = ModalBaseProps & {
  description: string;
  ticket: AdminTicketDetailsDto;
  onDisagree: () => void;
};
type TicketAIQuickDenialModalFormValues = {
  reply: string;
};

export function TicketAIQuickDenialModal({
  isOpened,
  onOpenChange,
  ticket,
  description,
  onDisagree,
}: TicketAIQuickDenialModalProps): React.ReactNode {
  const [isRejecting, setIsRejecting] = useState(false);
  const { t } = useTranslation();
  const formMethods = useForm<TicketAIQuickDenialModalFormValues>({
    defaultValues: {
      reply: "",
    },
  });
  const showFlashToast = useFlashToast();
  const ticketQueries = useTicketQueries();
  const { data: denialReplySuggestion, isPending: isPendingRejectReplySuggestion } = useQuery({
    ...ticketQueries.denialReplySuggestion(ticket.id, {
      additionalInfo: description,
    }),
    enabled: isOpened,
  });
  const changeStatusMutation = ticketMutations.useUpdateStatus();
  const ticketResponsibilityMutation = ticketMutations.useUpdateResponsibilitySuggestion(ticket.id);
  const postTicketCommentMutation = ticketMutations.useAddComment();
  useEffect(() => {
    if (isOpened && denialReplySuggestion && !formMethods.getValues("reply")) {
      formMethods.reset({
        reply: denialReplySuggestion.response,
      });
    }
  }, [isOpened, formMethods, denialReplySuggestion]);
  const onSubmit = async (data: TicketAIQuickDenialModalFormValues) => {
    setIsRejecting(true);

    // Change ticket to the first available `closed` status
    const closedStatus = ticket.possibleStatuses.find((status) => status.type === "closed")!;
    const promiseChangeTicketStatus = changeStatusMutation.mutateAsync({
      ticketId: ticket.id,
      data: {
        statusId: closedStatus.id,
      },
    });
    const promisePostTicketComment = postTicketCommentMutation.mutateAsync({
      ticketId: ticket.id,
      data: {
        content: data.reply,
        accessType: "public",
      },
    });
    const promisePostTicketResponsibility = ticketResponsibilityMutation.mutateAsync({
      rating: "good",
    });
    await Promise.all([promiseChangeTicketStatus, promisePostTicketComment, promisePostTicketResponsibility]).then(
      () => {
        showFlashToast({
          message: t("page.tickets.details.responsibility-suggestion-tenant-modal.deny-ticket.notification.success"),
          variant: "success",
        });
        onOpenChange(false);
        setIsRejecting(false);
      },
    );
  };
  const onClickDisagree = async () => {
    await ticketResponsibilityMutation.mutateAsync({
      rating: "bad",
    });
    onDisagree();
  };

  return (
    <Modal.Root
      data-testid="ai-responsibility-suggestion-modal"
      title={t("page.tickets.details.responsibility-suggestion-tenant-modal.title")}
      description={
        <Trans
          i18nKey="page.tickets.details.responsibility-suggestion-tenant-modal.description"
          values={{
            governmentData: t(
              "page.tickets.details.responsibility-suggestion-tenant-modal.description.government-data",
            ),
          }}
          components={{
            governmentData: (content) => (
              <Button
                variant="ghostPrimary"
                isExternal
                type="link"
                href="https://www.rijksoverheid.nl/onderwerpen/woning-huren/vraag-en-antwoord/welke-kosten-zijn-voor-de-huurder-en-welke-voor-de-verhuurder"
              >
                {content}
              </Button>
            ),
          }}
        />
      }
      badgeComponent={<Badge variant="special">{t("common.label.ai-driven")}</Badge>}
      isNested
      {...{
        isOpened,
        onOpenChange,
      }}
    >
      <Modal.Body>
        <Form.Root
          id={FORM_ID}
          fullWidth
          {...{
            formMethods,
            onSubmit,
          }}
        >
          <Stack gap="lg">
            <Stack gap="xs" align="start">
              <h3 className="text-heading-3">
                {t("page.tickets.details.responsibility-suggestion-tenant-modal.reason.subtitle")}
              </h3>
              <p className="text-body">{description}</p>
            </Stack>

            <Stack gap="xs" align="start">
              <h3 className="text-heading-3">
                {t("page.tickets.details.responsibility-suggestion-tenant-modal.deny-ticket.subtitle")}
              </h3>
              <p className="text-body">
                {t("page.tickets.details.responsibility-suggestion-tenant-modal.deny-ticket.description")}
              </p>
              <section className="relative mt-space-sm w-full">
                {isPendingRejectReplySuggestion && (
                  <aside className="absolute top-0 left-0 z-10 flex size-full items-center justify-center bg-background-default/60">
                    <Spinner size="2xl" />
                  </aside>
                )}

                <FormField
                  label={t("page.tickets.details.responsibility-suggestion-tenant-modal.denial-reply.label")}
                  hideLabel
                >
                  <FormTextArea<TicketAIQuickDenialModalFormValues>
                    name="reply"
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("page.tickets.details.responsibility-suggestion-tenant-modal.denial-reply.label"),
                      }),
                    }}
                  />
                </FormField>
              </section>
            </Stack>
          </Stack>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Button
          variant="outline"
          icon={<ThumbsDownIcon />}
          isLoading={ticketResponsibilityMutation.isPending && !isRejecting}
          disabled={isRejecting}
          onClick={onClickDisagree}
          data-testid="ai-responsibility-suggestion-modal-disagree-btn"
        >
          {t("page.tickets.details.responsibility-suggestion-tenant-modal.disagree.btn")}
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          isLoading={isRejecting}
          disabled={ticketResponsibilityMutation.isPending && !isRejecting}
          data-testid="ai-responsibility-suggestion-modal-reply-btn"
        >
          {t("page.tickets.details.responsibility-suggestion-tenant-modal.reply-and-deny.btn")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
